import {
  CarWheelPosition,
  CarWheelPositionText,
  type CarWheelInspectionPrePostJobRecord,
  type CarWheelSetTireWearRecord
} from '@navio/nsdk';
import type { TreadDepthThresholds } from 'noddi-util';

/** Grid corners in reading order: front row left→right, then rear row. */
export type TreadDepthCorner = 'frontLeft' | 'frontRight' | 'rearLeft' | 'rearRight';

/** Axles carry a single value on two-wheelers, where there is no left/right split. */
export type TreadDepthAxle = 'front' | 'rear';

/**
 * Tread depth in mm per position. A corner is omitted when that wheel has no
 * measurement; an axle key is used only when the vehicle measures the axle as a
 * whole instead of per corner.
 */
export type TreadDepthValues = Partial<Record<TreadDepthCorner | TreadDepthAxle, number>>;

export type TreadDepthGridSize = 'small' | 'medium';

/** Registered tire size per axle, e.g. `235/55R19`. Missing when the axle has no size. */
export type TireAxleDimensions = Partial<Record<TreadDepthAxle, string | null>>;

const POSITION_TO_KEY: Partial<Record<CarWheelPosition, TreadDepthCorner | TreadDepthAxle>> = {
  [CarWheelPosition.FRONT_LEFT]: 'frontLeft',
  [CarWheelPosition.FRONT_RIGHT]: 'frontRight',
  [CarWheelPosition.REAR_LEFT]: 'rearLeft',
  [CarWheelPosition.REAR_RIGHT]: 'rearRight',
  [CarWheelPosition.FRONT]: 'front',
  [CarWheelPosition.REAR]: 'rear'
};

// The same positions as text, for records that send `FRONT_LEFT` instead of the numeric choice.
const POSITION_TEXT_TO_KEY: Partial<Record<CarWheelPositionText, TreadDepthCorner | TreadDepthAxle>> = {
  [CarWheelPositionText.FRONT_LEFT]: 'frontLeft',
  [CarWheelPositionText.FRONT_RIGHT]: 'frontRight',
  [CarWheelPositionText.REAR_LEFT]: 'rearLeft',
  [CarWheelPositionText.REAR_RIGHT]: 'rearRight',
  [CarWheelPositionText.FRONT]: 'front',
  [CarWheelPositionText.REAR]: 'rear'
};

/**
 * Maps the latest per-wheel measurements of one wheel set into the shape
 * {@link TreadDepthGrid} renders. Positions outside the four corners and the two
 * axles (inner duals, middle axles on trucks) are dropped — the grid has no row
 * for them.
 */
export const treadDepthValuesFromWheelSet = (wheelSet: CarWheelSetTireWearRecord): TreadDepthValues => {
  const values: TreadDepthValues = {};

  for (const measurement of wheelSet.measurements) {
    const key = POSITION_TO_KEY[measurement.wheelPosition.value];
    if (key) {
      values[key] = measurement.treadDepth.value;
    }
  }

  return values;
};

export const treadDepthThresholdsFromWheelSet = (wheelSet: CarWheelSetTireWearRecord): TreadDepthThresholds => ({
  limitMm: wheelSet.limitThresholdMm,
  recommendedMm: wheelSet.recommendedThresholdMm
});

export const tireAxleDimensionsFromWheelSet = (wheelSet: CarWheelSetTireWearRecord): TireAxleDimensions => ({
  front: wheelSet.frontTireDimension,
  rear: wheelSet.rearTireDimension
});

/** Readings keyed by text position (latest tread depth endpoint, measurement sessions). Other positions are dropped. */
export const treadDepthValuesFromPositions = (
  readings: ReadonlyArray<{ position: CarWheelPositionText; valueMm: number }>
): TreadDepthValues => {
  const values: TreadDepthValues = {};

  for (const { position, valueMm } of readings) {
    const key = POSITION_TEXT_TO_KEY[position];
    if (key) {
      values[key] = valueMm;
    }
  }

  return values;
};

/**
 * An inspection records a size per wheel; the grid shows one per axle. The left wheel wins when the two
 * sides of an axle disagree, which only happens on a mis-registration.
 */
export const tireAxleDimensionsFromInspection = (
  inspection: CarWheelInspectionPrePostJobRecord
): TireAxleDimensions => ({
  front: inspection.frontLeftTireDimension || inspection.frontRightTireDimension || null,
  rear: inspection.rearLeftTireDimension || inspection.rearRightTireDimension || null
});
